import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { toggleModal } from 'apps/auth/actions/appAction';
import { sendBrazeEvent } from 'apps/auth/analytics/braze';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper } from 'apps/auth/components/common/Panel/styles';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import BasePage from 'apps/auth/components/utils/BasePage';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import {
    BR_BANNER_EXTRA_BONUS_CLOSED,
    BR_BANNER_EXTRA_BONUS_PRESSED_ARROW,
    BR_BANNER_EXTRA_BONUS_PRESSED_T_AND_C,
} from 'apps/auth/constants/braze/brazeEvents';
import { AUTH_BONUS_WITHDRAW, AUTH_PROFILE, AUTH_PROFILE_INVITE, RULES } from 'apps/auth/constants/UrlConstants';
import { USA } from 'apps/auth/constants/WellKnownCountries';
import { useApp, useAuth, useExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import { getHeaderText, getSecondaryText } from 'apps/auth/services/bonusService';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

export const AuthBonusPage = (props) => {
    const popupRef = useRef(null);
    const openExtraBonusModal = useRef();
    const { bonusBanner, showBonusRestriction } = useExtra();
    const { isShowModal } = useApp();
    const { countryId } = useAuth();
    const { learnMore, gotIt, insufficientBonusFunds, ...messages } = usePageMessages();
    const { bonusBalance } = useExtra();
    const bonusAmount = bonusBalance?.amount?.amount || bonusBalance?.formattedBonusAmount;

    useEffect(() => {
        const banner = Array.isArray(bonusBanner) ? bonusBanner.find((b) => b?.country_id === countryId) : bonusBanner;
        const cookieName = 'promo_strapi_banner_' + banner?.unique_banner_id;
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const expirationDate = new Date(banner?.expiration_date);
        if (banner?.country_id === countryId && now.getTime() < expirationDate?.getTime() && cookies.get(cookieName) === undefined) {
            const bannerUrl = banner?.icon?.data?.attributes?.url;
            props
                .toggleModal(
                    true,
                    <ModalComponent canCloseModal={false}>
                        <InfoPage
                            insideModalComponent
                            iconName={bannerUrl ? undefined : 'gift-80'}
                            iconColor={'primary'}
                            iconSrc={`${bannerUrl}`}
                            headerText={getHeaderText(banner)}
                            secondaryText={getSecondaryText(banner)}
                            buttonRowCaption={learnMore}
                            buttonRowType={'link'}
                            buttonRowTarget="_blank"
                            buttonRowHref={banner.term_url}
                            buttonRowOnClick={() => {
                                sendBrazeEvent(BR_BANNER_EXTRA_BONUS_PRESSED_T_AND_C);
                            }}
                            primaryButtonCaption={gotIt}
                            primaryButtonType={'link'}
                            primaryButtonOnClick={() => {
                                openExtraBonusModal.current = false;
                                cookies.set(cookieName, true, { path: '/', expires: expirationDate });
                                sendBrazeEvent(BR_BANNER_EXTRA_BONUS_CLOSED);
                                props.toggleModal(false);
                            }}
                        />
                    </ModalComponent>,
                    true,
                    false,
                )
                .then(() => {
                    openExtraBonusModal.current = true;
                });
        }
    }, [bonusBanner]);

    useEffect(() => {
        if (!isShowModal && openExtraBonusModal.current) {
            sendBrazeEvent(BR_BANNER_EXTRA_BONUS_PRESSED_ARROW);
        }
    }, [isShowModal]);

    const renderRules = () => {
        if (countryId === USA) {
            return <Button type="link" variant="row" href={`/en-us${RULES}`} caption={messages.bonusAboutRulesTitle} negativeMargin />;
        }

        return <Button type="link" variant="row" href={bonusBalance?.bonusOffer} target="_blank" caption={messages.bonusAboutRulesTitle} negativeMargin />;
    };

    return (
        <CommonPage backUrl={AUTH_PROFILE} hasBottomPanel showChat={false}>
            <Row indent="adaptive-16-24">
                <DiskBadge iconName="gift-48" diskSize="large" variant="secondary" />
            </Row>
            <Text variant="div" indent="4px" ellipsis>
                {messages.bonusBalanceTitle}
            </Text>
            <Text variant="div" indent="48px" size="display2">
                {bonusBalance?.formattedBonusAmount}{' '}
                <Text size="display2" smallCaps>
                    {bonusBalance?.currencyIso?.toLowerCase()}
                </Text>
            </Text>

            <Separator marginTop="0" marginBottom={bonusAmount > 0 ? '16px' : '48px'} marginTopDesktop="16px" />
            {bonusAmount > 0 && (
                <>
                    <Panel
                        isHoverable
                        type="reactLink"
                        to={AUTH_BONUS_WITHDRAW}
                        negativeMargin
                        onClick={(e) => {
                            if (showBonusRestriction) {
                                e.preventDefault();
                                popupRef.current?.open(
                                    <SimplePopup text={insufficientBonusFunds} popupRef={popupRef}>
                                        <PopupButton
                                            iconName="check"
                                            text={gotIt}
                                            onClick={() => {
                                                popupRef.current?.close();
                                            }}
                                        />
                                    </SimplePopup>,
                                );
                            }
                        }}
                    >
                        <DiskBadgeWrapper>
                            <DiskBadge variant="primary" iconName="arrow-right-dot" />
                        </DiskBadgeWrapper>
                        <Text color="primary">{messages.withdrawTextPanel}</Text>
                    </Panel>
                    <Separator marginTop="16px" marginBottom="48px" marginBottomDesktop="64px" />
                </>
            )}
            <Text variant="div" color="secondary" size="subheader" indent="adaptive-32-48">
                {messages.bonusBlock1Title}
            </Text>
            <Row indent="adaptive-16-24">
                <Text variant="div" indent="adaptive-16-24">
                    {messages.bonusBlock1Text1}
                </Text>
                <Text variant="div" indent="adaptive-16-24" color="secondary">
                    {messages.bonusBlock1Text2}
                </Text>
                <Button type="link" variant="secondary_emphasized" href={AUTH_PROFILE_INVITE} caption={messages.inviteFriendsButton} size={'tight'} />
            </Row>

            <Row>
                <Text variant="div" indent="adaptive-16-24">
                    {messages.bonusBlock1Text3}
                </Text>
                <Text variant="div" color="secondary">
                    {messages.bonusBlock1Text4}
                </Text>
            </Row>

            <Separator marginTop="32px" marginBottom="48px" marginTopDesktop="64px" marginBottomDesktop="64px" />

            <Text variant="div" color="secondary" size="subheader" indent="adaptive-32-48">
                {messages.bonusBlock2Title}
            </Text>
            <Row>
                <Text variant="div" indent="adaptive-16-24">
                    {messages.bonusBlock2Text1}
                </Text>
                <Text variant="div" indent="adaptive-16-24">
                    {messages.bonusBlock2Text2}
                </Text>
                <Text variant="div" indent="adaptive-16-24">
                    {messages.bonusBlock2Text3}
                </Text>
                {renderRules()}
            </Row>
            <PopupNew ref={popupRef} />
        </CommonPage>
    );
};

export default connect(null, { toggleModal })(BasePage(AuthBonusPage));
